"use client";

import { createContext, useCallback, useContext, useMemo, ReactNode } from "react";
import { IRobotContext } from "./types";
import { IRiskLevel } from "@portal/types/robot/IRiskLevel";
import { IRiskLevelProposalResponse } from "@portal/hooks/robot/useRiskLevelProposal/types";
import { ISavingProposalCustomerData } from "@portal/types/robot/ISavingAgreement";
import { ISavingAgreementAdditionalFunds } from "@portal/types/robot/ISavingAgreementAdditionalFunds";
import {
  AvailableLiquidity,
  Behaviour,
  Experience,
  RiskLevel,
  RiskAppetite,
  SavingOfTotalEconomy,
  SavingPeriod,
  ValuesEnum,
} from "@portal/types/robot/IRobotEnums";
import usePersistState from "@portal/hooks/usePersistState";

const SavingsAgreementConstants = {
  DEFAULT_MONTHLY_AMOUNT: 1000,
  DEFAULT_NUM_CALC_EACH_YEAR: 3,
  DEFAULT_MONTHS_STARTUP_AMOUNT: 1,
  DEFAULT_YEARS: 25,
  DEFAULT_ONE_TIME_DEPOSIT: 0,
};

const initialRiskLevelRequest: IRiskLevel = {
  savingPeriod: null,
  riskAppetite: null,
  behaviour: null,
  savingsToday: null,
  sharesToday: null,
  experience: null,
  relevantEduationOrJob: null,
  availableLiquidity: null,
  moreValuesThanDebt: null,
  noCreditCardDebt: null,
  savingOfTotalEconomy: null,
  sustainability: null,
  sustainabilitySfdr: null,
  sustainabilityEu: null,
  sustainabilityPai: null,
  originallyWantedSustainabilitySfdr: null,
  originallyWantedSustainabilityPai: null,
  originallyWantedSustainabilityEu: null,
  needMoreInfo: null,
  centerId: null,
};

const initialRiskLevelResponse: IRiskLevelProposalResponse = {
  isLoading: false,
  data: undefined,
  error: null,
};

const initialSavingProposalRequest: ISavingProposalCustomerData = {
  customerNumber: "",
  firstName: "",
  lastName: "",
  savingAgreementData: {
    riskLevel: null,
    oneTimeDeposit: SavingsAgreementConstants.DEFAULT_ONE_TIME_DEPOSIT,
    monthlyAmount: SavingsAgreementConstants.DEFAULT_MONTHLY_AMOUNT,
    sustainability: null,
    numOfYears: SavingsAgreementConstants.DEFAULT_YEARS,
    numOfcalculationsEachYear: SavingsAgreementConstants.DEFAULT_NUM_CALC_EACH_YEAR,
    numOfMonthsStartupAmount: SavingsAgreementConstants.DEFAULT_MONTHS_STARTUP_AMOUNT,
    savingAgreementAdditionalFunds: [] as ISavingAgreementAdditionalFunds[],
    comment1: "",
    comment2: "",
  },
};

const RobotContext = createContext<IRobotContext | undefined>(undefined);

export const RobotProvider = ({ children }: { children: ReactNode }) => {
  const [isDefaultLanguage, setIsDefaultLanguage] = usePersistState<boolean>("savingRobot-language", true);
  const [riskLevelRequest, setRiskLevelRequest] = usePersistState<IRiskLevel>("savingRobot-riskLevel", initialRiskLevelRequest);
  const [riskLevelResponse, setRiskLevelResponseState] = usePersistState<IRiskLevelProposalResponse>(
    "savingRobot-riskLevelResponse",
    initialRiskLevelResponse,
  );
  const [savingProposalRequest, setSavingProposalRequest] = usePersistState<ISavingProposalCustomerData>(
    "savingRobot-savingProposal",
    initialSavingProposalRequest,
  );
  const [returningUser, setReturningUser] = usePersistState<boolean>("savingRobot-returningUser", false);
  const [summedFee, setSummedFee] = usePersistState<number | undefined>("savingRobot-summedFee", undefined);

  const clearRobotData = useCallback(() => {
    setRiskLevelRequest(initialRiskLevelRequest);
    setRiskLevelResponseState(initialRiskLevelResponse);
    setSavingProposalRequest(initialSavingProposalRequest);
    setSummedFee(undefined);
    setReturningUser(false);
    setIsDefaultLanguage(true);
  }, [
    setRiskLevelRequest,
    setRiskLevelResponseState,
    setSavingProposalRequest,
    setReturningUser,
    setIsDefaultLanguage,
    setSummedFee,
  ]);

  const setCustomerNumber = useCallback(
    (v: string) => {
      setSavingProposalRequest(prev => {
        if (prev.customerNumber === v) {
          return prev;
        }
        return { ...prev, customerNumber: v };
      });
    },
    [setSavingProposalRequest],
  );

  const setCalculationsRiskLevel = useCallback(
    (v: RiskLevel) => {
      setSavingProposalRequest(prev => {
        if (prev.savingAgreementData.riskLevel === v) {
          return prev;
        }
        return { ...prev, savingAgreementData: { ...prev.savingAgreementData, riskLevel: v } };
      });
    },
    [setSavingProposalRequest],
  );

  const setMonthlyAmount = useCallback(
    (v: number) => {
      setSavingProposalRequest(prev => {
        if (prev.savingAgreementData.monthlyAmount === v) {
          return prev;
        }
        return { ...prev, savingAgreementData: { ...prev.savingAgreementData, monthlyAmount: v } };
      });
    },
    [setSavingProposalRequest],
  );

  const setNumOfYears = useCallback(
    (v: number) => {
      setSavingProposalRequest(prev => ({
        ...prev,
        savingAgreementData: { ...prev.savingAgreementData, numOfYears: v },
      }));
    },
    [setSavingProposalRequest],
  );

  const setNumOfCalculationsEachYear = useCallback(
    (v: number) => {
      setSavingProposalRequest(prev => ({
        ...prev,
        savingAgreementData: { ...prev.savingAgreementData, numOfcalculationsEachYear: v },
      }));
    },
    [setSavingProposalRequest],
  );

  const setNumOfMonthsStartupAmount = useCallback(
    (v: number) => {
      setSavingProposalRequest(prev => ({
        ...prev,
        savingAgreementData: { ...prev.savingAgreementData, numOfMonthsStartupAmount: v },
      }));
    },
    [setSavingProposalRequest],
  );

  const setCalculationsSustainability = useCallback(
    (v: boolean) => {
      setSavingProposalRequest(prev => ({
        ...prev,
        savingAgreementData: { ...prev.savingAgreementData, sustainability: v },
      }));
    },
    [setSavingProposalRequest],
  );

  const setOneTimeDeposit = useCallback(
    (v: number) => {
      setSavingProposalRequest(prev => ({
        ...prev,
        savingAgreementData: { ...prev.savingAgreementData, oneTimeDeposit: v },
      }));
    },
    [setSavingProposalRequest],
  );

  const value = useMemo<IRobotContext>(
    () => ({
      isDefaultLanguage,
      setIsDefaultLanguage,
      riskLevelRequest,
      riskLevelResponse,
      savingProposalRequest,
      returningUser,
      clearRobotData,
      summedFee,
      setExperience: (v: Experience) => {
        setRiskLevelRequest(prev => ({ ...prev, experience: v }));
        setReturningUser(true);
      },
      setRelevantEducationOrJob: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, relevantEduationOrJob: v }));
        setReturningUser(true);
      },
      setSavingPeriod: (v: SavingPeriod) => {
        setRiskLevelRequest(prev => ({ ...prev, savingPeriod: v }));
        setReturningUser(true);
      },
      setRiskAppetite: (v: RiskAppetite | null) => {
        setRiskLevelRequest(prev => ({ ...prev, riskAppetite: v }));
        setReturningUser(true);
      },
      setBehaviour: (v: Behaviour | null) => {
        setRiskLevelRequest(prev => ({ ...prev, behaviour: v }));
        setReturningUser(true);
      },
      setAvailableLiquidity: (v: AvailableLiquidity) => {
        setRiskLevelRequest(prev => ({ ...prev, availableLiquidity: v }));
        setReturningUser(true);
      },
      setSavingsToday: (v: ValuesEnum) => {
        setRiskLevelRequest(prev => ({ ...prev, savingsToday: v }));
        setReturningUser(true);
      },
      setSharesToday: (v: ValuesEnum) => {
        setRiskLevelRequest(prev => ({ ...prev, sharesToday: v }));
        setReturningUser(true);
      },
      setMoreValuesThanDebt: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, moreValuesThanDebt: v }));
        setReturningUser(true);
      },
      setNoCreditCardDebt: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, noCreditCardDebt: v }));
        setReturningUser(true);
      },
      setSavingOfTotalEconomy: (v: SavingOfTotalEconomy) => {
        setRiskLevelRequest(prev => ({ ...prev, savingOfTotalEconomy: v }));
        setReturningUser(true);
      },
      setNeedMoreInfo: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, needMoreInfo: v }));
        setReturningUser(true);
      },
      setSustainability: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, sustainability: v }));
        setReturningUser(true);
      },
      setSustainabilitySfdr: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, sustainabilitySfdr: v }));
        setReturningUser(true);
      },
      setSustainabilityPai: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, sustainabilityPai: v }));
        setReturningUser(true);
      },
      setSustainabilityEu: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, sustainabilityEu: v }));
        setReturningUser(true);
      },
      setOriginallyWantedSustainabilitySfdr: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, originallyWantedSustainabilitySfdr: v }));
        setReturningUser(true);
      },
      setOriginallyWantedSustainabilityPai: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, originallyWantedSustainabilityPai: v }));
        setReturningUser(true);
      },
      setOriginallyWantedSustainabilityEu: (v: boolean) => {
        setRiskLevelRequest(prev => ({ ...prev, originallyWantedSustainabilityEu: v }));
        setReturningUser(true);
      },
      setCustomerNumber,
      setCalculationsRiskLevel,
      setMonthlyAmount: setMonthlyAmount,
      setNumOfYears: setNumOfYears,
      setNumOfCalculationsEachYear: setNumOfCalculationsEachYear,
      setNumOfMonthsStartupAmount: setNumOfMonthsStartupAmount,
      setCalculationsSustainability: setCalculationsSustainability,
      setOneTimeDeposit: setOneTimeDeposit,
      setRiskLevelResponse: setRiskLevelResponseState,
      setSummedFee,
    }),
    [
      isDefaultLanguage,
      setIsDefaultLanguage,
      riskLevelRequest,
      riskLevelResponse,
      savingProposalRequest,
      returningUser,
      clearRobotData,
      summedFee,
      setCustomerNumber,
      setCalculationsRiskLevel,
      setMonthlyAmount,
      setNumOfYears,
      setNumOfCalculationsEachYear,
      setNumOfMonthsStartupAmount,
      setCalculationsSustainability,
      setOneTimeDeposit,
      setRiskLevelResponseState,
      setSummedFee,
      setRiskLevelRequest,
      setReturningUser,
    ],
  );

  return <RobotContext.Provider value={value}>{children}</RobotContext.Provider>;
};

export const useRobot = (): IRobotContext => {
  const context = useContext(RobotContext);
  if (!context) {
    throw new Error("useRobot must be used within RobotProvider");
  }
  return context;
};
